import React, { useCallback, useEffect, useRef, useState } from 'react'

import Button from '@material-ui/core/Button'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt'
import { toast } from 'react-toastify'

import ConfirmationModal from '../ConfirmationModal'
import toastError from '../../errors/toastError'
import api from '../../services/api'

const LAST_CHECK_KEY = 'dockerUpdatesLastCheck'
const DISMISSED_KEY = 'dockerUpdatesDismissed'
const DAY_MS = 24 * 60 * 60 * 1000

const useStyles = makeStyles(theme => ({
  paper: {
    width: '100%',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    borderRadius: 12,
    borderLeft: `6px solid ${theme.palette.warning.main}`,
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.dark_400.main
        : theme.palette.white_100.main,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch'
    }
  },
  info: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  title: {
    fontSize: '1rem',
    fontWeight: 700,
    color:
      theme.palette.type === 'dark'
        ? theme.palette.dark_50.main
        : theme.palette.gray_400.main
  },
  subtitle: {
    fontSize: '0.85rem',
    color:
      theme.palette.type === 'dark'
        ? theme.palette.dark_50.main
        : theme.palette.gray_400.main
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  icon: {
    color: theme.palette.warning.main
  }
}))

const DockerUpdatesBanner = ({ isSuper }) => {
  const classes = useStyles()
  const [checkResult, setCheckResult] = useState(null)
  const [checking, setChecking] = useState(false)
  const [startingUpdate, setStartingUpdate] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [dockerUnavailable, setDockerUnavailable] = useState(false)
  const mountedRef = useRef(true)

  const wasDismissedToday = useCallback(() => {
    const dismissedAt = Number(localStorage.getItem(DISMISSED_KEY) || 0)
    return dismissedAt && Date.now() - dismissedAt < DAY_MS
  }, [])

  const runCheck = useCallback(
    async manual => {
      setChecking(true)
      try {
        const { data } = await api.get('/docker/check-updates')
        if (!mountedRef.current) return
        setDockerUnavailable(false)
        setCheckResult(data)
        localStorage.setItem(LAST_CHECK_KEY, String(Date.now()))
        if (data.updatesAvailable > 0) {
          localStorage.removeItem(DISMISSED_KEY)
          setDismissed(false)
        } else if (manual) {
          toast.success('Todos os containers estão atualizados.')
        }
      } catch (err) {
        if (!mountedRef.current) return
        if (err?.response?.status === 503) {
          setDockerUnavailable(true)
        } else if (manual) {
          toastError(err)
        }
      } finally {
        if (mountedRef.current) {
          setChecking(false)
        }
      }
    },
    []
  )

  useEffect(() => {
    mountedRef.current = true

    if (!isSuper) {
      return undefined
    }

    const lastCheck = Number(localStorage.getItem(LAST_CHECK_KEY) || 0)
    if (!lastCheck || Date.now() - lastCheck > DAY_MS) {
      runCheck(false)
    }

    return () => {
      mountedRef.current = false
    }
  }, [isSuper, runCheck])

  if (!isSuper || dockerUnavailable) {
    return null
  }

  const updatesAvailable = checkResult?.updatesAvailable || 0
  const outdatedContainers = (checkResult?.containers || []).filter(
    container => container.updateAvailable === true
  )
  const showBanner =
    updatesAvailable > 0 && !dismissed && !wasDismissedToday()

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()))
    setDismissed(true)
  }

  const handleUpdateAll = async () => {
    setStartingUpdate(true)
    try {
      const { data } = await api.post('/docker/update-all')
      toast.info(
        `${data.message} (${data.scheduled} containers agendados)`,
        { autoClose: 10000 }
      )
      setDismissed(true)
      localStorage.setItem(DISMISSED_KEY, String(Date.now()))
    } catch (err) {
      toastError(err)
    } finally {
      setStartingUpdate(false)
    }
  }

  return (
    <>
      <Paper className={classes.paper} elevation={0}>
        <SystemUpdateAltIcon className={classes.icon} />
        <div className={classes.info}>
          <Typography className={classes.title}>
            {updatesAvailable > 0
              ? `${updatesAvailable} atualização(ões) de container disponível(is)`
              : 'Containers Docker'}
          </Typography>
          <Typography className={classes.subtitle}>
            {updatesAvailable > 0
              ? `Pendentes: ${outdatedContainers
                .map(container => container.name)
                .join(', ')}.`
              : 'Nenhuma atualização pendente.'}{' '}
            {checkResult?.checkedAt &&
              `Última verificação: ${new Date(
                checkResult.checkedAt
              ).toLocaleString()}.`}
          </Typography>
        </div>
        <div className={classes.actions}>
          <Button
            size="small"
            color="primary"
            onClick={() => runCheck(true)}
            disabled={checking || startingUpdate}
          >
            {checking ? 'Verificando...' : 'Verificar agora'}
          </Button>
          {showBanner && (
            <>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={() => setConfirmOpen(true)}
                disabled={checking || startingUpdate}
              >
                {startingUpdate ? 'Iniciando...' : 'Atualizar todos'}
              </Button>
              <Button
                size="small"
                onClick={handleDismiss}
                disabled={checking || startingUpdate}
              >
                Depois
              </Button>
            </>
          )}
        </div>
      </Paper>

      {confirmOpen && (
        <ConfirmationModal
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleUpdateAll}
          title="Atualizar todos os containers"
        >
          Todos os containers em execução serão atualizados (pull + recriação),
          incluindo o próprio backend e suas dependências. O sistema poderá
          reiniciar algumas vezes durante o processo. Deseja continuar?
        </ConfirmationModal>
      )}
    </>
  )
}

export default DockerUpdatesBanner
